import { Controller } from "stimulus"

const decode = value => {
  const text = value.replace(/-/g, "+").replace(/_/g, "/")
  return Uint8Array.from(atob(text), c => c.charCodeAt(0))
}
const encode = value => btoa(String.fromCharCode(...new Uint8Array(value)))
  .replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "")

export default class extends Controller {
  static targets = ["status", "name", "trigger", "confirmation"]

  connect() { this.busy = false }
  disconnect() { if (this.operation) this.operation.abort() }

  async register(event) { await this.perform(event, true) }
  async login(event) { await this.perform(event, false) }

  async post(url, data = {}) {
    const csrf = document.querySelector('meta[name="csrf-token"]')
    const request = new AbortController()
    const timer = window.setTimeout(() => request.abort(), 15000)
    try {
      const response = await fetch(url, {
        signal: request.signal,
        method: "POST", credentials: "same-origin",
        headers: { "Content-Type": "application/json", "Accept": "application/json",
          ...(csrf ? { "X-CSRF-Token": csrf.content } : {}) },
        body: JSON.stringify(data)
      })
      if (response.status === 429) throw new Error("rate_limited")
      if (response.redirected || response.status === 401) throw new Error("session_expired")
      const result = await response.json()
      if (!response.ok) throw new Error(result.error === "reauthentication_required" ? result.error : "verification")
      return result
    } catch (error) {
      if (error.name === "AbortError") throw new Error("network_timeout")
      throw error
    } finally { window.clearTimeout(timer) }
  }

  async perform(event, registration) {
    event.preventDefault()
    if (this.busy || !this.hasStatusTarget) return
    const button = event.currentTarget
    const status = this.statusTarget
    this.busy = true
    button.disabled = true
    button.setAttribute("aria-busy", "true")
    if (this.hasConfirmationTarget) this.confirmationTarget.hidden = true
    status.textContent = "Sekojiet norādēm ierīces logā. Ja izvēlaties telefonu, tam jābūt blakus."
    try {
      if (!window.PublicKeyCredential || !window.isSecureContext) throw new Error("unavailable")
      const options = await this.post(this.data.get(registration ? "registration-options" : "authentication-options"))
      options.challenge = decode(options.challenge)
      if (registration) {
        options.user.id = decode(options.user.id)
        options.excludeCredentials = (options.excludeCredentials || []).map(c => ({ ...c, id: decode(c.id) }))
      } else {
        options.allowCredentials = (options.allowCredentials || []).map(c => ({ ...c, id: decode(c.id) }))
      }
      options.timeout = 120000
      this.operation = new AbortController()
      const timeout = window.setTimeout(() => this.operation.abort(), 120000)
      let credential
      try {
        credential = await navigator.credentials[registration ? "create" : "get"]({ publicKey: options, signal: this.operation.signal })
      } finally { window.clearTimeout(timeout) }
      if (!credential) throw new Error("unavailable")
      const response = { clientDataJSON: encode(credential.response.clientDataJSON) }
      if (registration) {
        response.attestationObject = encode(credential.response.attestationObject)
        response.transports = credential.response.getTransports ? credential.response.getTransports() : []
      } else {
        response.authenticatorData = encode(credential.response.authenticatorData)
        response.signature = encode(credential.response.signature)
        response.userHandle = credential.response.userHandle ? encode(credential.response.userHandle) : null
      }
      const result = await this.post(this.data.get(registration ? "register" : "authenticate"), {
        credential: { id: credential.id, rawId: encode(credential.rawId), type: credential.type, response },
        name: registration && this.hasNameTarget ? this.nameTarget.value : undefined
      })
      window.location.assign(result.redirect)
    } catch (error) {
      if (["reauthentication_required", "session_expired"].includes(error.message)) {
        status.textContent = registration ? "Identitātes apstiprinājuma laiks ir beidzies. Apstipriniet identitāti vēlreiz, tad turpiniet iestatīšanu." : "Pieslēgšanās sesija ir beigusies. Atveriet pieslēgšanās lapu no jauna."
        if (this.hasConfirmationTarget) this.confirmationTarget.hidden = false
      } else if (error.message === "rate_limited") {
        status.textContent = "Pārāk daudz mēģinājumu. Uzgaidiet brīdi un mēģiniet vēlreiz."
      } else if (error.message === "network_timeout") {
        status.textContent = "Lapa neatbildēja laikā. Pārbaudiet interneta savienojumu un mēģiniet vēlreiz."
      } else if (["NotAllowedError", "AbortError", "TimeoutError"].includes(error.name)) {
        status.textContent = "Mēģinājums tika atcelts vai tam beidzās laiks. Varat mēģināt vēlreiz. Ja izmantojat telefonu, pārbaudiet Bluetooth abās ierīcēs."
      } else if (error.message === "unavailable" || error.name === "NotSupportedError") {
        status.textContent = registration ? "Šajā pārlūkā vai ierīcē šo veidu nevar izmantot. Izvēlieties kodu lietotni vai mēģiniet citā ierīcē." : "Šajā ierīcē piekļuves atslēga nav pieejama. Izmantojiet paroli un savu apstiprināšanas veidu."
      } else {
        status.textContent = registration ? "Neizdevās izveidot piekļuves atslēgu. Mēģiniet vēlreiz vai izvēlieties kodu lietotni." : "Neizdevās pārbaudīt piekļuves atslēgu. Mēģiniet vēlreiz vai izmantojiet paroli."
      }
      if (registration) button.textContent = "Mēģināt vēlreiz"
    } finally {
      this.busy = false
      this.operation = null
      button.disabled = false
      button.removeAttribute("aria-busy")
    }
  }
}
